import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { css } from 'styled-components';
import PrestyledSpinner from '../components/Spinner';
import { COLORS, MEDIUM, REGULAR } from '../utils/styled';
function kindCss({ kind = 'primary' }) {
    switch (kind) {
        case 'primary':
            return css `
                background-color: ${COLORS.ButtonPrimary};
                border: none;

                &,
                &:link,
                &:active,
                &:visited {
                    color: ${COLORS.Primary};
                }

                &:hover,
                &:focus {
                    background-color: ${COLORS.ButtonPrimaryHover};
                }

                &:active {
                    background-color: ${COLORS.ButtonPrimaryActive};
                }

                &[disabled] {
                    background-color: ${COLORS.ButtonPrimaryDisabled};
                    color: rgba(255, 255, 255, 0.5);
                }
            `;
        case 'secondary':
            return css `
                background-color: ${COLORS.ButtonSecondary};
                border: none;

                &,
                &:link,
                &:active,
                &:visited {
                    color: ${COLORS.White};
                }

                &:hover,
                &:focus {
                    background-color: ${COLORS.ButtonSecondaryHover};
                }

                &:active {
                    background-color: ${COLORS.ButtonSecondaryActive};
                }

                &[disabled] {
                    background-color: ${COLORS.ButtonSecondaryDisabled};
                    color: rgba(82, 82, 82, 0.5);
                }
            `;
        case 'link':
            return css `
                background-color: transparent;
                border-color: transparent;
                font-weight: ${REGULAR};
                padding: 0 0.5rem !important; /* ignore "size" classes */

                &,
                &:link,
                &:active,
                &:visited {
                    color: #525252;
                }

                &:hover,
                &:focus {
                    color: #323232;
                    transform: scale(1) translate3d(0, 0, 0);
                }

                &:active {
                    color: rgba(82, 82, 82, 0.8);
                    transform: scale(1) translate3d(0, 0, 0);
                }

                &[disabled] {
                    border-color: transparent;
                    color: rgba(82, 82, 82, 0.5);
                }
            `;
        case 'transparent':
            return css `
                background-color: transparent;
                border-color: transparent;

                &,
                &:link,
                &:active,
                &:visited {
                    color: #525252;
                }

                &:hover,
                &:focus {
                    background-color: transparent;
                    border-color: transparent;
                }

                &:active {
                    background-color: transparent;
                    border-color: transparent;
                }

                &[disabled] {
                    background-color: transparent;
                    border-color: transparent;
                    color: rgba(82, 82, 82, 0.5);
                }
            `;
        case 'destructive':
            return css `
                background-color: ${COLORS.ButtonDestructive};
                border: none;

                &,
                &:link,
                &:active,
                &:visited {
                    color: ${COLORS.White};
                }

                &:hover,
                &:focus {
                    background-color: ${COLORS.ButtonDestructive};
                }

                &:active {
                    background-color: ${COLORS.ButtonDestructive};
                }

                &[disabled] {
                    background-color: ${COLORS.ButtonDestructive};
                    color: rgba(82, 82, 82, 0.5);
                }
            `;
        default:
            return null;
    }
}
const paddingMap = {
    mini: {
        none: '0',
        small: '0 0.5rem',
        normal: '0 1.5rem',
    },
    normal: {
        none: '0',
        small: '0 1rem',
        normal: '0 2.625rem',
    },
    big: {
        none: '0',
        small: '0 1rem',
        normal: '0 3rem',
    },
};
const getPaddingCssForSize = (size, padding) => paddingMap[size][padding] ?? paddingMap[size].normal;
function sizeCss({ size = 'normal', padding = 'normal', }) {
    switch (size) {
        case 'mini':
            return css `
                height: 3rem;
                min-width: 3rem;
                padding: ${getPaddingCssForSize(size, padding)};
                font-size: 1rem;
                line-height: 1.25rem;
            `;
        case 'normal':
            return css `
                height: 3.75rem;
                min-width: 3.75rem;
                padding: ${getPaddingCssForSize(size, padding)};
                font-size: 1rem;
                line-height: 1.25rem;
            `;
        case 'big':
            return css `
                height: 3.75rem;
                min-width: 3.75rem;
                padding: ${getPaddingCssForSize(size, padding)};
                font-size: 1.25rem;
                line-height: 1.5rem;
                border-radius: 16px !important;
            `;
        default:
            return null;
    }
}
function shapeCss({ shape = 'normal' }) {
    switch (shape) {
        case 'normal':
            return css `
                border-radius: 12px;
            `;
        case 'pill':
            return css `
                border-radius: 100px;
            `;
        default:
            return null;
    }
}
function variantCss({ variant }) {
    switch (variant) {
        case 'dark':
            return css `
                background: ${COLORS.ButtonDark};
                backdrop-filter: blur(50px);
                -webkit-backdrop-filter: blur(50px);

                &,
                &:link,
                &:active,
                &:visited {
                    color: ${COLORS.White};
                }

                &:hover,
                &:focus {
                    color: inherit;
                    background-color: ${COLORS.ButtonDark};
                }

                &:active {
                    background-color: ${COLORS.ButtonDark};
                }

                &[disabled] {
                    background-color: ${COLORS.ButtonSecondaryDisabled};
                    color: rgba(82, 82, 82, 0.5);
                }
            `;
        case 'glow':
            return css `
                box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.05), inset 0px -1px 1px rgba(255, 255, 255, 0.1), inset 0px 1px 1px rgba(255, 255, 255, 0.25);
            `;
        case 'destructive-inverted':
            return css `
                &,
                &:link,
                &:active,
                &:visited {
                    color: ${COLORS.ButtonDestructive};
                }
            `;
        default:
            return null;
    }
}
const Icon = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
`;
const Container = styled.div `
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;
const Content = styled.div `
`;
function getButtonAttrs({ children, waiting = false, disabled = false, onClick, kind = 'primary', icon = null, ...props }) {
    return {
        ...props,
        disabled: disabled || waiting,
        children: (_jsxs(Container, { children: [icon && _jsx(Icon, { children: icon }), children && _jsx(Content, { children: children }), waiting && (_jsx(Spinner, { color: kind === 'primary' ? 'white' : 'gray' }))] })),
        tabIndex: disabled ? -1 : 0,
        onClick: (e) => {
            if (disabled) {
                e.preventDefault();
                return;
            }
            if (e.currentTarget instanceof HTMLElement) {
                // Make sure we make the button lose focus after click
                e.currentTarget.blur();
            }
            onClick?.(e);
        },
    };
}
export const Button = styled.button.attrs(getButtonAttrs) `
    -webkit-font-smoothing: subpixel-antialiased;
    align-items: center;
    backface-visibility: hidden;
    background-color: transparent;
    border: 1px solid;
    border-radius: 44px;
    cursor: pointer;
    display: inline-flex;
    font-family: inherit;
    font-weight: ${MEDIUM};
    justify-content: center;
    letter-spacing: 0;
    padding: 0;
    position: relative;
    text-align: center;
    transform: translate3d(0, 0, 0);
    transition: 200ms ease-in-out;
    transition-property: border-color, background-color, color, transform;
    user-select: none;
    white-space: nowrap;

    &,
    &:active,
    &:visited,
    &:hover,
    &:focus {
        text-decoration: none;
        outline: none;
    }

    &:hover,
    &:focus {
        transition-duration: 5ms;
        transform: scale(1.05) translate3d(0, 0, 0);
    }

    &:active {
        transform: scale(0.95) translate3d(0, 0, 0);
    }

    ${Content} {
        margin-left: ${({ icon }) => (icon ? '0.5rem' : '0')};
    }

    &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        transform: scale(1) translate3d(0, 0, 0);
    }

    ${kindCss}

    ${sizeCss}

    ${shapeCss}

    ${variantCss}
`;
const Spinner = styled(PrestyledSpinner) `
    width: 1rem;
    height: 1rem;
    margin-left: 0.5rem;
`;
